import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import ShareModal from "../games/auxiliary/ShareModal";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../utils/contexts/UserContext";

const GAME_BASE_URL = process.env.REACT_APP_GAME_SERVER_URL;
const SOCKET_RECONNECTION_ATTEMPTS = 10;
const SOCKET_RECONNECTION_DELAY = 1000;
const SOCKET_CONNECTION_TIMEOUT = 40000;

export default function ShareInvite({
  code = "345 908",
  onShare,
  onJoin,
  onPlayRandom,
  qrValue = "345908",
  gameId,
  gameName = "chess",
  onPlayActiveUsers,
  onClose,
}) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shouldJoin, setShouldJoin] = useState(false);
  const socketRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const userContext = React.useContext(UserContext);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const hasJoinedRef = useRef(false);
  const joinAttemptRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);

  const cleanupSocket = () => {
    if (socketRef.current) {
      socketRef.current.off("connect");
      socketRef.current.off("disconnect");
      socketRef.current.off("connect_error");
      socketRef.current.off("lobby-joined");
      socketRef.current.off("opponent-joined-lobby");
      socketRef.current.off("joinGameError");
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  };

  const initializeSocket = () => {
    return new Promise((resolve, reject) => {
      cleanupSocket();

      console.log("Initializing socket connection...");

      const token = sessionStorage.getItem("token");
      const parsedToken = JSON.parse(token);

      const socket = io(GAME_BASE_URL, {
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: SOCKET_RECONNECTION_ATTEMPTS,
        reconnectionDelay: SOCKET_RECONNECTION_DELAY,
        timeout: SOCKET_CONNECTION_TIMEOUT,
        query: {
          userId: userContext?.user?._id,
          token: `Bearer ${parsedToken.accessToken}`,
        },
      });

      connectionTimeoutRef.current = setTimeout(() => {
        console.error("Socket connection timeout");
        reject(new Error("Socket connection timeout"));
        cleanupSocket();
      }, SOCKET_CONNECTION_TIMEOUT);

      socket.on("connect", () => {
        console.log("Socket connected:", socket.id);
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setConnectionError(false);
        setIsConnected(true);
        setReconnecting(false);

        if (shouldJoin && !hasJoinedRef.current) {
          emitJoinGame();
        }

        resolve(socket);
      });

      socket.on("disconnect", () => {
        console.log("Socket disconnected");
        setIsConnected(false);
        setReconnecting(true);
        hasJoinedRef.current = false;
      });

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
        setIsConnected(false);
        setReconnecting(false);
        hasJoinedRef.current = false;
        reject(error);
      });

      socket.on("lobby-joined", (userID, lobbyCode, response) => {
        console.log("Lobby joined:", { userID, lobbyCode, response });
        if (response && response.gameName) {
          hasJoinedRef.current = true;
          // Use opponent ID from response if available, otherwise use a placeholder
          const opponentId = response.opponentId || "waitingForOpponent";

          // Format game name properly
          const formattedGameName = response.gameName
            ? response.gameName.charAt(0).toUpperCase() +
              response.gameName.slice(1)
            : "Chess";

          // Extract lobbyId correctly if qrValue is an object
          const lobbyId =
            typeof qrValue === "object" && qrValue._id ? qrValue._id : qrValue;

          const path = `/games/selected-user/${gameId}/${userID}/${opponentId}/${lobbyId}/${lobbyCode}/${formattedGameName}`;
          console.log("Navigating to:", path);
          console.log("Navigation parameters:", {
            gameId,
            userID,
            opponentId,
            lobbyId,
            lobbyCode,
            gameName: formattedGameName,
          });

          navigate(path);
        }
      });

      socket.on("opponent-joined-lobby", (creatorId, gameName, lobbyCode) => {
        console.log("Opponent joined lobby:", {
          creatorId,
          gameName,
          lobbyCode,
        });
        // Handle opponent joining notification if needed
      });

      socket.on("joinGameError", (error) => {
        console.error("Error joining game:", error);
        hasJoinedRef.current = false;
        setShouldJoin(false);
        setConnectionError(true);
      });

      socketRef.current = socket;
    });
  };

  const handleConnectionRetry = () => {
    setConnectionError(false);
    setIsLoading(true);

    initializeSocket()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Connection retry failed:", error);
        setConnectionError(true);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    initializeSocket()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Failed to connect socket:", error);
        setConnectionError(true);
        setIsLoading(false);
      });

    return () => {
      cleanupSocket();
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, [userContext?.user?._id]);

  const emitJoinGame = () => {
    if (!socketRef.current?.connected) {
      console.log("Socket not connected, cannot join game");
      return;
    }

    if (hasJoinedRef.current) {
      console.log("Already joined game, not emitting again");
      return;
    }

    const playerId = userContext?.user?._id;
    if (!playerId) {
      console.error("No player ID available");
      return;
    }

    const lobbyCode = code.replace(/\s/g, "");
    console.log("Emitting lobby-joined with:", { playerId, lobbyCode });
    hasJoinedRef.current = true;

    // Store the join attempt data
    joinAttemptRef.current = { playerId, lobbyCode };

    // Emit the lobby-joined event
    socketRef.current.emit("lobby-joined", playerId, lobbyCode);

    // Set a timeout to handle navigation if no response is received
    setTimeout(() => {
      if (hasJoinedRef.current && joinAttemptRef.current) {
        console.log("No response received, navigating with stored data");
        const { playerId, lobbyCode } = joinAttemptRef.current;

        // Format game name properly
        const formattedGameName = gameName
          ? gameName.charAt(0).toUpperCase() + gameName.slice(1)
          : "Chess";

        // Extract lobbyId correctly if qrValue is an object
        const lobbyId =
          typeof qrValue === "object" && qrValue._id ? qrValue._id : qrValue;

        // Need to wait for opponent to be selected through ActiveUsers instead
        // Redirect to page where user can select an opponent
        const path = `/games/selected-user/${gameId}/${playerId}/waitingForOpponent/${lobbyId}/${lobbyCode}/${formattedGameName}`;
        console.log("Navigating to opponent selection page:", path);
        console.log("Navigation parameters:", {
          gameId,
          playerId,
          opponentId: "waitingForOpponent",
          lobbyId,
          lobbyCode,
          gameName: formattedGameName,
        });

        navigate(path);
      }
    }, 2000); // Wait 2 seconds for server response
  };

  const handleSelectUser = () => {
    const lobbyCode = code.replace(/\s/g, "");

    const path = `/games/selected-player/${lobbyCode}`;
    // console.log("Navigating to random user selection:", path);
    navigate(path);
  };

  const handleJoinGame = () => {
    const playerId = userContext?.user?._id;
    const lobbyCode = code.replace(/\s/g, "");

    if (!playerId) {
      console.error("No player ID available");
      return;
    }

    if (socketRef.current?.connected) {
      // First try socket approach
      emitJoinGame();
    } else {

      // Ensure lobbyId is correct - if qrValue is an object or string
      const lobbyId =
        typeof qrValue === "object" && qrValue._id ? qrValue._id : qrValue;


      // Since we don't have an opponent, redirect to opponent selection screen
      navigate(
        `/games/selected-user/${gameId}/${playerId}/waitingForOpponent/${lobbyId}/${lobbyCode}/${gameName}`
      );

      // Also try to reconnect socket for future use
      setShouldJoin(true);
      socketRef.current.connect();
    }
  };

  if (connectionError) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#130828",
          color: "#FFF",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 16px",
        }}
      >
        <div
          style={{
            color: "#FFF",
            fontSize: 18,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          Lost connection to the game server
        </div>
        <button
          onClick={handleConnectionRetry}
          style={{
            backgroundColor: "#FFCC00",
            padding: "15px",
            borderRadius: 8,
            width: "80%",
            textAlign: "center",
            marginBottom: 10,
            border: "none",
            color: "#000000",
            fontSize: 16,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Retry Connection
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: "transparent",
            border: "2px solid #FFCC00",
            padding: "15px",
            borderRadius: 8,
            width: "80%",
            textAlign: "center",
            color: "#FFCC00",
            fontSize: 16,
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Exit Game
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#130828] text-white flex flex-col items-center justify-start px-4 py-8">
      {/* Header */}
        <div className="w-full flex items-center mb-8">
          <button
            onClick={onClose}
            className="text-white text-2xl bg-transparent border-none"
          >
            &larr;
          </button>
          <span className="flex-1 text-center font-bold tracking-wider bg-gradient-to-r from-[#C6BCFF] to-[#3F9217] text-transparent bg-clip-text">
            Share PIN & Play Invite
          </span>
        </div>

        {/* QR and Code Block */}
      <div className="rounded-xl px-6 py-6 mb-6 flex flex-col items-center">
        <div
          onClick={() => navigator.clipboard.writeText(code)}
          className="text-3xl font-bold tracking-widest mb-2 cursor-pointer"
        >
          {code}
        </div>
        <div
          onClick={() => navigator.clipboard.writeText(code)}
          className="text-sm text-gray-400 mb-6 cursor-pointer"
        >
          Tap code to copy
        </div>

        <div className="text-center my-4">
          <button
            onClick={() => setShowShareModal(true)}
            className="w-40 rounded-md border border-[#56920D] bg-transparent text-[#FFF] font-semibold text-lg py-2 flex items-center justify-center gap-2 mb-6"
          >
            <span className="text-xl">✈️</span> SHARE
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <button
        onClick={() => {
          const playerId = userContext?.user?._id;
          const lobbyCode = code.replace(/\s/g, "");
          if (typeof onPlayRandom === "function") {
            onPlayRandom(gameId, playerId, qrValue, lobbyCode, gameName);
          } else {
            const lobbyId =
              typeof qrValue === "object" && qrValue._id
                ? qrValue._id
                : qrValue;
            const path = `/games/selected-user/${gameId}/${playerId}/random/${lobbyId}/${lobbyCode}/${gameName}`;
            navigate(path);
          }
        }}
        disabled={isLoading}
        className={`w-full bg-[#e49047] text-white font-bold text-lg rounded-md py-3 mb-8 ${
          isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#aa2d82]"
        }`}
      >
      Play Online users
      </button>
      <button
        onClick={() => {
          console.log("QR Value:", qrValue);
          const lobbyId = qrValue._id || qrValue;
          const playerId = userContext?.user?._id;
          console.log("Player ID:", userContext?.user);
          const lobbyCode = code.replace(/\s/g, "");
          navigate(`/games/quick-match/${lobbyCode}/${playerId}/${gameName}/${lobbyId}/${gameId}`);
        }}
        disabled={isLoading}
        className={`w-full bg-[#BE3593] text-white font-bold text-lg rounded-md py-3 mb-8 ${
          isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#aa2d82]"
        }`}
      >
     Play Quick Match
      </button>

      <button
        onClick={() => {
          const playerId = userContext?.user?._id;
          const lobbyCode = code.replace(/\s/g, "");
          onPlayActiveUsers(gameId, playerId, qrValue, gameName, lobbyCode);
        }}
        disabled={isLoading}
        className={`w-full bg-[#FBBC04] text-black font-bold text-lg rounded-md py-3 mb-8 ${
          isLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-yellow-400"
        }`}
      >
     Play My Invitees
      </button>

      {/* Footer Message */}
      <div className="text-sm text-white text-opacity-70 text-center mt-auto">
        PIN & QR Code are unique and different for each competition, you can
        invite your friends to join the competition
      </div>

      {/* Share Modal */}
      <ShareModal
        modalProps={{
          visible: showShareModal,
          onHide: () => setShowShareModal(false),
          size: "sm",
        }}
        gameName={gameName}
        lobbyCode={code}
      />
    </div>
  );
}
